/* MUI */
import { Avatar, AvatarGroup, Box, Divider, Typography } from '@mui/material';

/* Shared */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import {
    LightTooltip,
    TooltipPlacement
} from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Application as AppType } from 'application/Shared/types/application';
import { Application, ApplicationIcon } from 'objects/Application';
import React from 'react';

/**
 * Renders an application bubble with an avatar containing an application icon
 * @param {Object} props - Component props
 * @param {Application} props.app - Application object containing application data
 * @returns {JSX.Element} Application bubble component
 */
const AppBubble = ({ app }: { app: AppType }): JSX.Element => (
    <Application data={app}>
        <Box
            display="flex"
            my="0"
            alignItems="center"
            data-testid="appBubbleWrapper"
        >
            <Avatar
                sx={{
                    backgroundColor: 'avatar.appsAvatar.backgroundColor',
                    marginRight: '-4px'
                }}
                data-testid="appBubbleAvatar"
            >
                <ApplicationIcon sx={{ height: '16px', width: '16px' }} />
            </Avatar>
        </Box>
    </Application>
);

/**
 * Renders a group of application bubbles with tooltips showing application details
 * @param {Object} props - Component props
 * @param {string} [props.align] - Alignment of the bubbles ('left' or 'right')
 * @param {number} [props.max] - Maximum number of bubbles to display before grouping
 * @param {string} [props.tooltipPlacement] - Placement of the tooltip relative to the bubbles
 * @param {Array<Application>} props.applications - Array of Application objects to display
 * @returns {JSX.Element|null} ApplicationBubbles component or null if no applications
 */
export const ApplicationBubbles = ({
    align,
    max,
    tooltipPlacement,
    applications
}: {
    align?: string;
    max?: number;
    tooltipPlacement?: string;
    applications: AppType[];
}): JSX.Element | null => {
    if (!applications) {
        return null;
    }

    const sortedApps = applications
        .filter(Boolean)
        .sort((a: AppType, b: AppType): number =>
            `${a?.name}`.localeCompare(b?.name)
        );

    return (
        <Box
            data-testid="applicationBubbles"
            display="flex"
            style={{
                justifyContent: align === 'right' ? 'flex-end' : 'flex-start'
            }}
        >
            <LightTooltip
                title={
                    <Box
                        sx={{
                            fontSize: FontSizes[0],
                            fontWeight: 600,
                            maxHeight: '160px',
                            overflowX: 'hidden',
                            overflowY: 'scroll'
                        }}
                    >
                        {sortedApps.map((app) => (
                            <Box paddingY="4px" key={app.id}>
                                <Application data={app} nameHasLink>
                                    <ApplicationNameLinkCell
                                        nameNoTruncate
                                        withCategory
                                        categoryNoTruncate
                                        categorySize="smaller"
                                    />
                                </Application>
                                <Divider sx={{ marginY: '4px' }} />
                            </Box>
                        ))}
                    </Box>
                }
                placement={tooltipPlacement as TooltipPlacement}
                arrow
                slotProps={{
                    tooltip: {
                        sx: {
                            minWidth: '400px'
                        }
                    }
                }}
            >
                <AvatarGroup
                    className="no-print"
                    data-testid="appAvatarGroup"
                    max={max}
                    sx={{
                        '& .MuiAvatar-root': {
                            border: '1px solid',
                            borderColor: 'border.appBubbles'
                        },
                        '&.MuiAvatarGroup-root .MuiAvatarGroup-avatar': {
                            backgroundColor:
                                'avatar.appsAvatarGroup.backgroundColor'
                        },
                        alignItems: 'self-start',
                        justifyContent: 'center'
                    }}
                >
                    {sortedApps.map((app) => (
                        <AppBubble app={app} key={app.id} />
                    ))}
                </AvatarGroup>
            </LightTooltip>
            {/* When printed we only want to show count of Applications */}
            <Typography className="print-only">{sortedApps.length}</Typography>
        </Box>
    );
};
